import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Primitives";
import { toast } from "sonner";
import { Plus, Trash, BookOpen } from "@phosphor-icons/react";

const CLASS_GROUPS = ["Class 9", "Class 10", "Class 11", "Class 12", "B.Com", "B.Sc", "B.CA"];

export default function AdminSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ name: "", class_group: "Class 10", chapters: "" });
  const [chInputs, setChInputs] = useState({});

  const load = () => api.get("/subjects").then(r => setSubjects(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/subjects", {
        name: form.name,
        class_group: form.class_group,
        chapters: form.chapters.split("\n").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Subject created");
      setForm({ name: "", class_group: "Class 10", chapters: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addChapter = async (sid) => {
    const name = chInputs[sid];
    if (!name) return;
    await api.post(`/subjects/${sid}/chapters`, { name });
    setChInputs({ ...chInputs, [sid]: "" });
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete subject?")) return;
    await api.delete(`/subjects/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader eyebrow="Curriculum" title="Subjects & chapters" subtitle="Define the taxonomy used by question banks and tests." />

      <Section title="Add subject">
        <form onSubmit={create} className="card-academic p-6 grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Subject name</label>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="subj-name"
              className="w-full bg-ivory border border-stone px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta" />
          </div>
          <div>
            <label className="overline block mb-2">Class / Stream</label>
            <select value={form.class_group} onChange={e => setForm({ ...form, class_group: e.target.value })} data-testid="subj-class"
              className="w-full bg-ivory border border-stone px-4 py-3">
              {CLASS_GROUPS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">Chapters (one per line)</label>
            <textarea rows={4} value={form.chapters} onChange={e => setForm({ ...form, chapters: e.target.value })} data-testid="subj-chapters"
              className="w-full bg-ivory border border-stone px-4 py-3 font-mono text-sm" placeholder={"Real Numbers\nPolynomials\nQuadratic Equations"} />
          </div>
          <button className="btn-primary md:col-span-2 flex items-center gap-2 justify-center" data-testid="subj-submit"><Plus size={16} /> Create subject</button>
        </form>
      </Section>

      <Section title={`All subjects (${subjects.length})`}>
        {subjects.length === 0 ? <EmptyState /> : (
          <div className="grid md:grid-cols-2 gap-4">
            {subjects.map(s => (
              <div key={s.id} className="card-academic p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="overline">{s.class_group}</div>
                    <h3 className="font-serif text-2xl text-forest flex items-center gap-2"><BookOpen size={20} weight="duotone" /> {s.name}</h3>
                  </div>
                  <button onClick={() => remove(s.id)} className="text-mossy hover:text-terracotta" data-testid={`del-subj-${s.id}`}><Trash size={18} /></button>
                </div>
                <ul className="text-sm space-y-1 mb-3">
                  {s.chapters.map(c => (
                    <li key={c.id} className="flex items-center gap-2 text-ink">
                      <span className="w-1 h-1 bg-terracotta" /> {c.name}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <input
                    placeholder="New chapter"
                    value={chInputs[s.id] || ""}
                    onChange={e => setChInputs({ ...chInputs, [s.id]: e.target.value })}
                    className="flex-1 bg-bone border border-stone px-3 py-2 text-sm"
                    data-testid={`new-chapter-${s.id}`}
                  />
                  <button onClick={() => addChapter(s.id)} className="btn-secondary text-xs px-4 py-2" data-testid={`add-chapter-${s.id}`}>Add</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
